import { ButtonGroup, makeStyles, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import cn from 'classnames';
import { addDays, addMonths, endOfDay, startOfDay } from 'date-fns';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _pick from 'lodash/pick';
import eventsAPI, { Event as EventType } from '../../api/events';
import foundersAPI, { FounderId } from '../../api/founders';
import { Role } from '../../api/user/Role';
import { RouterContext } from '../../contexts/router-context';
import { useSessionsContext } from '../../contexts/sessions-context';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { formatDateApi } from '../../utils/date';
import { decodeQuery, encodeQuery } from '../../utils/functions';
import { Text, Link } from '../common';
import List from '../common/list';
import SessionCard from '../list-cards/session-card';

interface DateOption {
  label: string;
  key: string;
  range: () => {
    startDate: Date;
    endDate: Date;
    sortOrder?: string;
  };
}

interface FilterValue {
  time?: string;
  venture?: {
    id: string;
    ventureName: string;
  };
}

const useStyles = makeStyles(() => ({
  buttonGroup: {
    '& > button': {
      width: 90,
      padding: '4px 16px',
    },
  },
  disabledButtonGroup: {
    pointerEvents: 'none',
    opacity: 0.8,
    transition: 'opacity 0.3s ease-in-out',
  },
  centerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    textAlign: 'center',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 10,
    boxSizing: 'border-box',
  },
  emptyStateBtn: {
    marginTop: 16,
  },
  ventureName: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    fontSize: 18,
  },
}));

const dateOptions: DateOption[] = [
  {
    label: 'Relevant',
    key: 'relevant',
    range: () => ({
      startDate: startOfDay(addDays(new Date(), -7)),
      endDate: endOfDay(addDays(new Date(), 7)),
    }),
  },
  {
    label: 'Past',
    key: 'past',
    range: () => ({
      startDate: startOfDay(addMonths(new Date(), -6)),
      endDate: endOfDay(new Date()),
      sortOrder: 'desc',
    }),
  },
  {
    label: 'Future',
    key: 'future',
    range: () => ({
      startDate: startOfDay(new Date()),
      endDate: endOfDay(addMonths(new Date(), 6)),
    }),
  },
];

export default function SessionsList() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { identityid, hasRole, hasAccessToAction } = useContext(UserContext);
  const { prevPath } = useContext(RouterContext);
  const { sessions, resetKey, setSessions } = useSessionsContext();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const prevSessionId = useMemo(() => {
    if (prevPath?.includes?.('/sessions/')) {
      const withoutQuery = prevPath.split('?')[0];
      const withoutHash = withoutQuery.split('#')[0];
      const splitPath = withoutHash.split('/');
      const sessionsIndex = splitPath.indexOf('sessions');
      const sessionId = splitPath[sessionsIndex + 1];

      if (sessionId && sessionId.length === 36) {
        return `card-${sessionId}`;
      }
    }
  }, [prevPath]);

  const searchParams = useMemo(
    () => parseQuery(location.search),
    [location.search],
  );

  const { selectedDateOption, venture } = useMemo(() => {
    let selectedDateOption;
    let venture;

    if (searchParams.filter) {
      const decodedFilter = decodeQuery(searchParams.filter as string);
      const parsedQuery = _pick(decodedFilter || {}, [
        'time',
        'venture',
      ]) as FilterValue;
      selectedDateOption = dateOptions.find((o) => o.key === parsedQuery.time);
      venture = parsedQuery.venture;
    }
    return {
      selectedDateOption: selectedDateOption || dateOptions[0],
      venture,
    };
  }, [searchParams.filter]);

  const rangeFilter = useMemo(() => {
    return selectedDateOption.range();
  }, [selectedDateOption]);

  const prepareNewURL = useCallback(
    (nextSearchParams) => {
      return `${location.pathname}${
        Object.keys(nextSearchParams).length
          ? `?${stringifyQuery(nextSearchParams)}`
          : ''
      }`;
    },
    [location.pathname],
  );

  const handleFilterUpdate = useCallback(
    (key: string) => {
      const clearSearchParams = {
        ...searchParams,
        filter: encodeQuery({
          time: key,
        }),
      };
      history.replace(prepareNewURL(clearSearchParams));
    },
    [history, prepareNewURL, searchParams],
  );

  useEffect(() => {
    const loadSessions = async () => {
      try {
        setIsLoading(true);
        setSessions([]);

        let loadedSessions: EventType[] = [];

        if (venture) {
          loadedSessions = await eventsAPI.searchByFilterV2({
            ventureId: venture.id,
          });
        } else {
          const { startDate, endDate, sortOrder } = rangeFilter || {};
          const eventFilter = {
            startDate: formatDateApi(startDate),
            endDate: formatDateApi(endDate),
            sortOrder,
          };

          switch (selectedDateOption.key) {
            case 'relevant':
              if (hasRole(Role.Mentor)) {
                loadedSessions = await eventsAPI.getMentorRelevantEvents();
              } else if (hasRole(Role.Founder)) {
                loadedSessions = await eventsAPI.getFounderRelevantEvents();
              } else {
                loadedSessions = await eventsAPI.searchByFilterV2(eventFilter);
              }
              break;
            case 'past':
            case 'future':
              if (hasRole(Role.Founder)) {
                loadedSessions = await foundersAPI.getEventsDetailsByDataRange(
                  identityid as FounderId,
                  eventFilter.startDate,
                  eventFilter.endDate,
                  eventFilter.sortOrder,
                );
              } else {
                loadedSessions = await eventsAPI.searchByFilterV2(eventFilter);
              }
              break;
          }
        }

        setSessions(loadedSessions);
      } catch (e: any) {
        if (e?.response?.status === 404 || e?.message === '404') {
          setSessions([]);
          return;
        }

        console.error(e);
        const errorMessage =
          e?.response?.data?.message || 'Internal server error';
        setLoadError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRole, rangeFilter, selectedDateOption, resetKey]);

  return (
    <List
      prevId={prevSessionId}
      loadError={loadError}
      isLoading={isLoading}
      filter={
        venture ? (
          <Text variant='normal' bold className={classes.ventureName}>
            {venture.ventureName}
          </Text>
        ) : (
          <ButtonGroup
            color='primary'
            aria-label='contained primary button group'
            className={cn(classes.buttonGroup, {
              [classes.disabledButtonGroup]: isLoading,
            })}>
            {dateOptions.map((option) => (
              <Button
                key={option.key}
                onClick={() => handleFilterUpdate(option.key)}
                variant={
                  selectedDateOption.key === option.key
                    ? 'contained'
                    : 'outlined'
                }>
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        )
      }>
      {!sessions.length ? (
        <div className={classes.centerBlock}>
          <Text variant='normal' testid='session-table-no-items'>
            {hasAccessToAction('session.create') ? (
              <>
                This panel contains a list of sessions.
                <br />
                We were not able to find information you requested, but feel
                free to add something new!
              </>
            ) : (
              <>
                There are no sessions scheduled yet with your participation.{' '}
                <br /> Please reach out to the Program Manager to get a session
                with a venture on your calendar!
              </>
            )}
          </Text>
          {hasAccessToAction('session.create') && (
            <Button
              component={Link}
              to={Pages.NEW_SESSIONS}
              className={classes.emptyStateBtn}
              startIcon={<AddIcon />}
              variant='contained'
              color='primary'>
              Session
            </Button>
          )}
        </div>
      ) : (
        <div className={classes.listContainer} key={selectedDateOption.key}>
          {sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              cardId={`card-${session.id}`}
            />
          ))}
        </div>
      )}
    </List>
  );
}
